/**
 * Manage the vendors used for internal (white-label) tracking.
 *
 * Vendors with an API integration (Atlantic, ICL) show an "API" badge and can
 * have their real timeline pulled into the console. Vendors without one
 * (Aramex, Network Courier, ...) are tracked from staff entries — the
 * suggestion engine still works for them via the stage phrases.
 */
import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Switch from "@mui/material/Switch";
import Chip from "@mui/material/Chip";
import { Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useNotification } from "context/notification.context";
import { getTrackingVendors, addTrackingVendor, updateTrackingVendor } from "network/service";

const blank = { name: "", key: "", has_api: false, api_vendor_key: "", is_active: true };

export default function VendorsModal(props) {
  const { showNotification } = useNotification();
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState({});

  const load = () => {
    getTrackingVendors()
      .then((r) => setRows(r.data || []))
      .catch(() => showNotification("error", "Error", "Failed to load vendors"));
  };

  useEffect(() => {
    if (props.isopen) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isopen]);

  const setField = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = (row, i) => {
    const payload = {
      name: row.name,
      key: (row.key || row.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "_"),
      has_api: !!row.has_api,
      api_vendor_key: row.has_api ? row.api_vendor_key || row.key : "",
      is_active: row.is_active !== false,
    };
    if (!payload.name) return showNotification("error", "Error", "Vendor name is required");
    setSaving((s) => ({ ...s, [i]: true }));
    const req = row._id ? updateTrackingVendor(row._id, payload) : addTrackingVendor(payload);
    req
      .then(() => { showNotification("success", "Saved", `${payload.name} saved`); load(); })
      .catch((err) => showNotification("error", "Error", err?.response?.data?.message || "Save failed"))
      .finally(() => setSaving((s) => ({ ...s, [i]: false })));
  };

  return (
    <Dialog open={props.isopen} onClose={props.handleclose} maxWidth="md" fullWidth>
      <DialogTitle>
        Internal tracking vendors
        <MDTypography variant="caption" display="block" color="text">
          Vendors with an API can have their real timeline pulled in. Others are tracked from staff entries.
        </MDTypography>
      </DialogTitle>
      <DialogContent dividers>
        <Table size="small">
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              {["Vendor", "Key", "API", "API vendor key", "Active", ""].map((h) => (
                <TableCell key={h} sx={{ fontWeight: "bold" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={row._id || `new-${i}`}>
                <TableCell><MDInput value={row.name || ""} onChange={(e) => setField(i, { name: e.target.value })} /></TableCell>
                <TableCell sx={{ width: 130 }}>
                  <MDInput value={row.key || ""} onChange={(e) => setField(i, { key: e.target.value })} />
                </TableCell>
                <TableCell>
                  <MDBox display="flex" alignItems="center" gap={0.5}>
                    <Switch checked={!!row.has_api} onChange={(e) => setField(i, { has_api: e.target.checked })} />
                    {row.has_api && <Chip label="API" size="small" color="success" />}
                  </MDBox>
                </TableCell>
                <TableCell sx={{ width: 140 }}>
                  <MDInput
                    value={row.api_vendor_key || ""}
                    disabled={!row.has_api}
                    placeholder="atlantic / icl"
                    onChange={(e) => setField(i, { api_vendor_key: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <Switch checked={row.is_active !== false} onChange={(e) => setField(i, { is_active: e.target.checked })} />
                </TableCell>
                <TableCell>
                  <MDButton size="small" color="success" disabled={saving[i]} onClick={() => save(row, i)}>
                    <Icon>save</Icon>
                  </MDButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <MDButton variant="outlined" color="info" onClick={() => setRows((rs) => [...rs, { ...blank }])}>
          <Icon>add</Icon>&nbsp;Add vendor
        </MDButton>
        <MDButton variant="text" color="secondary" onClick={props.handleclose}>Close</MDButton>
      </DialogActions>
    </Dialog>
  );
}
